/**
 * OFC Scoring
 *
 * Scoring rules:
 * - Compare each row (front, middle, back) between players
 * - Win row = +1, Lose row = -1, Tie = 0
 * - Win all 3 rows = scoop bonus (+3)
 * - Royalties: Bonus points for strong hands (trips+)
 * - Foul penalty: -6 points if back < middle < front order violated
 */
import { isBoardComplete, isBoardFouled } from './board';
import { getRankIndex } from './card';
import { Label, OFCVariant, Rank, RowPosition } from './enums';
import { compareEntries, evaluateFrontHand, evaluateHighHand, evaluateLowHand, getRankCounts } from './hands';
import {
  DEUCE_TO_SEVEN_MIDDLE_ROYALTIES,
  DEUCE_TO_SEVEN_ROYALTIES,
  FOUL_PENALTY,
  SCOOP_BONUS,
  STANDARD_ROYALTIES,
} from './royalties';
import { getBoard } from './state';
import { Board, GameState, Row, RoyaltyTable, SettlementResult } from './types';
import { isDeuceToSevenVariant } from './variants';

/** Get royalty lookup key for front row (e.g., "QQ", "AAA") */
export function getFrontRoyaltyKey(row: Row): string | null {
  if (row.cards.length !== 3) return null;

  const counts = getRankCounts(row.cards);
  const entries = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);

  if (entries[0][1] === 3) {
    return entries[0][0].repeat(3);
  }

  if (entries[0][1] === 2) {
    const pairRank = entries[0][0];
    const pairIndex = getRankIndex(pairRank);
    if (pairIndex >= getRankIndex(Rank.SIX)) {
      return pairRank.repeat(2);
    }
  }

  return null;
}

/** Calculate front row royalty (pairs 66+, trips) */
export function calculateFrontRoyalty(row: Row, table: RoyaltyTable): number {
  const key = getFrontRoyaltyKey(row);
  if (!key) return 0;
  return table.front.get(key) ?? 0;
}

/** Calculate middle row royalty (trips+) */
export function calculateMiddleRoyalty(row: Row, table: RoyaltyTable): number {
  if (row.cards.length !== 5) return 0;

  const entry = evaluateHighHand(row.cards);
  return table.middle.get(entry.label) ?? 0;
}

/** Calculate back row royalty (straight+) */
export function calculateBackRoyalty(row: Row, table: RoyaltyTable): number {
  if (row.cards.length !== 5) return 0;

  const entry = evaluateHighHand(row.cards);
  return table.back.get(entry.label) ?? 0;
}

/** Calculate royalty for any row position */
export function calculateRowRoyalty(row: Row, position: RowPosition, table: RoyaltyTable): number {
  switch (position) {
    case RowPosition.FRONT:
      return calculateFrontRoyalty(row, table);
    case RowPosition.MIDDLE:
      return calculateMiddleRoyalty(row, table);
    case RowPosition.BACK:
      return calculateBackRoyalty(row, table);
  }
}

/** Calculate total royalties for a board (0 if fouled) */
export function calculateBoardRoyalties(board: Board, table: RoyaltyTable, variant?: OFCVariant): number {
  if (isBoardFouled(board, variant)) return 0;

  return (
    calculateFrontRoyalty(board.front, table) +
    calculateMiddleRoyalty(board.middle, table) +
    calculateBackRoyalty(board.back, table)
  );
}

/** Calculate 2-7 middle row royalty (low hands) */
export function calculateMiddleLowRoyalty(row: Row): number {
  if (row.cards.length !== 5) return 0;

  const entry = evaluateLowHand(row.cards);

  if (entry.label !== Label.HIGH_CARD) return 0;

  const highRank = row.cards.map((c) => c.rank).sort((a, b) => getRankIndex(b) - getRankIndex(a))[0];

  return DEUCE_TO_SEVEN_MIDDLE_ROYALTIES.get(highRank) ?? 0;
}

/** Compare two rows: positive if row1 wins, negative if row2 wins, 0 if tie */
export function compareRows(row1: Row, row2: Row, position: RowPosition): number {
  if (position === RowPosition.FRONT) {
    const entry1 = evaluateFrontHand(row1.cards);
    const entry2 = evaluateFrontHand(row2.cards);
    return compareEntries(entry1, entry2);
  }

  const entry1 = evaluateHighHand(row1.cards);
  const entry2 = evaluateHighHand(row2.cards);
  return compareEntries(entry1, entry2);
}

/** Settle points between two boards (handles fouls, scoop, royalties) */
export function settleHeadToHead(
  board1: Board,
  board2: Board,
  table: RoyaltyTable,
  variant?: OFCVariant,
): SettlementResult {
  const fouled1 = isBoardFouled(board1, variant);
  const fouled2 = isBoardFouled(board2, variant);

  if (fouled1 && fouled2) {
    return {
      points: 0,
      breakdown: { front: 0, middle: 0, back: 0, scoop: 0, royalties: 0 },
    };
  }

  if (fouled1) {
    const opponentRoyalties = calculateBoardRoyalties(board2, table, variant);
    return {
      points: FOUL_PENALTY - opponentRoyalties,
      breakdown: { front: -1, middle: -1, back: -1, scoop: -3, royalties: -opponentRoyalties },
    };
  }

  if (fouled2) {
    const myRoyalties = calculateBoardRoyalties(board1, table, variant);
    return {
      points: -FOUL_PENALTY + myRoyalties,
      breakdown: { front: 1, middle: 1, back: 1, scoop: 3, royalties: myRoyalties },
    };
  }

  const frontResult = Math.sign(compareRows(board1.front, board2.front, RowPosition.FRONT));
  const middleResult = Math.sign(compareRows(board1.middle, board2.middle, RowPosition.MIDDLE));
  const backResult = Math.sign(compareRows(board1.back, board2.back, RowPosition.BACK));

  const rowsWon = frontResult + middleResult + backResult;

  let scoopBonus = 0;
  if (rowsWon === 3) scoopBonus = SCOOP_BONUS;
  else if (rowsWon === -3) scoopBonus = -SCOOP_BONUS;

  const myRoyalties = calculateBoardRoyalties(board1, table, variant);
  const oppRoyalties = calculateBoardRoyalties(board2, table, variant);
  const royaltyDiff = myRoyalties - oppRoyalties;

  const totalPoints = rowsWon + scoopBonus + royaltyDiff;

  return {
    points: totalPoints,
    breakdown: {
      front: frontResult,
      middle: middleResult,
      back: backResult,
      scoop: scoopBonus,
      royalties: royaltyDiff,
    },
  };
}

/** Settle points for 2+ players (each pair is settled head-to-head) */
export function settleMultiway(boards: Board[], table: RoyaltyTable, variant?: OFCVariant): Map<number, number> {
  const results = new Map<number, number>();

  for (const board of boards) {
    results.set(board.playerIndex, 0);
  }

  for (let i = 0; i < boards.length; i++) {
    for (let j = i + 1; j < boards.length; j++) {
      const settlement = settleHeadToHead(boards[i], boards[j], table, variant);

      const player1 = boards[i].playerIndex;
      const player2 = boards[j].playerIndex;

      results.set(player1, (results.get(player1) ?? 0) + settlement.points);
      results.set(player2, (results.get(player2) ?? 0) - settlement.points);
    }
  }

  return results;
}

/** Calculate final scores for all players in a game state */
export function calculateScores(state: GameState): Map<number, number> {
  const boards: Board[] = [];
  for (let i = 0; i < state.playerCount; i++) {
    const board = getBoard(state, i);
    if (board) {
      boards.push(board);
    }
  }
  const table = isDeuceToSevenVariant(state.variant) ? DEUCE_TO_SEVEN_ROYALTIES : STANDARD_ROYALTIES;
  return settleMultiway(boards, table, state.variant);
}

/** Get foul status for all players */
export function getPlayerFoulStatus(state: GameState): Map<number, boolean> {
  const result = new Map<number, boolean>();
  for (let i = 0; i < state.playerCount; i++) {
    const board = getBoard(state, i);
    if (board && isBoardComplete(board)) {
      result.set(i, isBoardFouled(board, state.variant));
    } else {
      result.set(i, false);
    }
  }
  return result;
}
